<template>
    <div>
        <v-card class="mx-auto mt-4" max-width="98%" flat color="transparent">
            <v-toolbar color="#37474F" dark rounded class="mb-6 elevation-4">
                <v-toolbar-title class="font-weight-bold">
                    <v-icon left>mdi-shield-check</v-icon>
                    유저 이벤트 검수
                </v-toolbar-title>
                <v-spacer />

                <v-select
                    v-model="reviewTypeFilter"
                    :items="reviewTypeFilterOptions"
                    item-text="text"
                    item-value="value"
                    dark dense hide-details outlined
                    prepend-inner-icon="mdi-filter-variant"
                    class="review-type-select mr-4"
                    style="max-width: 160px;"
                    @change="LoadList"
                ></v-select>

                <v-btn-toggle v-model="viewMode" mandatory class="mode-toggle mr-4 elevation-2" @change="LoadList" dense active-class="active-mode">
                    <v-btn value="not_verify" dark>
                        <v-icon left small>mdi-filter-variant</v-icon>
                        미인증 내역
                    </v-btn>
                    <v-btn value="all" dark>
                        <v-icon left small>mdi-format-list-bulleted</v-icon>
                        전체 내역
                    </v-btn>
                </v-btn-toggle>

                <v-divider vertical class="mx-2" inset></v-divider>

                <div class="d-flex align-center ml-2">
                    <v-checkbox
                        v-if="viewMode === 'not_verify'"
                        v-model="selectAll"
                        label="전체 선택"
                        hide-details
                        dark
                        class="mt-0 mr-4"
                        @change="ToggleAll"
                    ></v-checkbox>
                    <v-btn
                        v-if="viewMode === 'not_verify'"
                        color="success"
                        class="mr-2"
                        @click="ProcessSelection('Y')"
                        :disabled="selectedPids.length === 0"
                        elevation="2"
                    >
                        선택 인증 ({{ selectedPids.length }})
                    </v-btn>
                    <v-btn
                        v-if="viewMode === 'not_verify'"
                        color="error"
                        @click="ProcessSelection('R')"
                        :disabled="selectedPids.length === 0"
                        elevation="2"
                    >
                        선택 반려
                    </v-btn>
                </div>
            </v-toolbar>

            <div v-if="loadingData" class="text-center py-10">
                <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
            </div>

            <v-row v-else-if="review_list.length > 0">
                <v-col v-for="item in review_list" :key="item.PID" cols="12" sm="6" md="4" lg="3" xl="2">
                    <v-card
                        class="review-card elevation-2 mb-4"
                        :class="{
                            'selected-card': IsSelected(item.PID) && viewMode === 'not_verify',
                            'readonly-card': viewMode !== 'not_verify'
                        }"
                        @click="viewMode === 'not_verify' ? ToggleSelect(item.PID) : null"
                    >
                        <v-hover v-slot="{ hover }">
                            <div class="image-container">
                                <v-img
                                    :src="GetItemImage(item)"
                                    height="200"
                                    class="grey lighten-2"
                                    contain
                                >
                                    <template v-slot:placeholder>
                                        <v-row class="fill-height ma-0" align="center" justify="center">
                                            <v-icon large color="grey lighten-4">mdi-image-off</v-icon>
                                        </v-row>
                                    </template>
                                    <v-fade-transition>
                                        <div
                                            v-if="IsSelected(item.PID) && viewMode === 'not_verify'"
                                            class="selection-overlay d-flex align-center justify-center text-h2 white--text"
                                        >
                                            <v-icon color="white" x-large>mdi-check-circle</v-icon>
                                        </div>
                                    </v-fade-transition>
                                </v-img>
                                <div v-if="hover" class="zoom-preview shadow-lg">
                                    <v-img :src="GetItemImage(item)" min-width="400" max-width="800" max-height="600" class="white" contain elevation="12"></v-img>
                                </div>
                            </div>
                        </v-hover>

                        <v-card-text class="pt-4 pb-2">
                            <div class="d-flex justify-space-between align-start mb-1">
                                <div class="text-subtitle-1 font-weight-bold text-truncate" style="max-width: 70%">
                                    {{ UserLabel(item) }}
                                </div>
                                <v-chip :color="GetStatusColor(item.VERIFIED)" dark x-small class="font-weight-bold">
                                    {{ GetStatusText(item.VERIFIED) }}
                                </v-chip>
                            </div>
                            <div class="mb-2">
                                <v-chip :color="GetReviewTypeColor(item.REVIEW_TYPE)" x-small outlined class="font-weight-bold">
                                    <v-icon x-small left>{{ item.REVIEW_TYPE === 'MARKET' ? 'mdi-storefront' : 'mdi-widgets' }}</v-icon>
                                    {{ GetReviewTypeText(item.REVIEW_TYPE) }}
                                </v-chip>
                            </div>
                            <div class="text-caption grey--text text--darken-1">PID: {{ item.PID }}</div>
                        </v-card-text>

                        <v-divider></v-divider>

                        <v-card-actions class="pa-2">
                            <v-btn text small color="primary" @click.stop="ViewDetail(item)">상세보기</v-btn>
                            <v-spacer></v-spacer>
                            <template v-if="IsPending(item.VERIFIED)">
                                <v-btn color="success" small depressed @click.stop="ProcessItem(item.PID, 'Y')">인증</v-btn>
                                <v-btn color="error" small depressed @click.stop="ProcessItem(item.PID, 'R')">반려</v-btn>
                            </template>
                        </v-card-actions>
                    </v-card>
                </v-col>
            </v-row>

            <v-row v-else class="fill-height py-16" align="center" justify="center">
                <v-col cols="12" class="text-center">
                    <v-icon size="100" color="grey lighten-1">mdi-folder-open-outline</v-icon>
                    <div class="text-h6 grey--text mt-4">항목이 없습니다.</div>
                </v-col>
            </v-row>
        </v-card>

        <!-- Detail Dialog -->
        <v-dialog v-model="detailDialog" max-width="700" :fullscreen="$vuetify.breakpoint.xsOnly">
            <v-card v-if="selectedDetailItem">
                <v-card-title class="white--text" style="background-color:#37474F">
                    리뷰 상세 — {{ UserLabel(selectedDetailItem) }}
                    <v-spacer></v-spacer>
                    <v-btn icon dark @click="detailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text class="pa-6">
                    <div class="d-flex mb-4 text-subtitle-2 grey--text">
                        <span>유저: {{ UserLabel(selectedDetailItem) }}</span>
                        <v-divider vertical class="mx-3"></v-divider>
                        <span>PID: {{ selectedDetailItem.PID }}</span>
                    </div>
                    <v-img :src="GetItemImage(selectedDetailItem)" max-height="400" contain class="grey lighten-3 rounded"></v-img>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions class="pa-4" v-if="IsPending(selectedDetailItem.VERIFIED)">
                    <v-btn color="success" block large @click="ProcessItem(selectedDetailItem.PID, 'Y')">이 리뷰 인증하기</v-btn>
                    <v-btn color="error" class="ml-2" large @click="ProcessItem(selectedDetailItem.PID, 'R')">반려</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Reject Reason Dialog -->
        <v-dialog v-model="rejectDialog" max-width="500" :fullscreen="$vuetify.breakpoint.xsOnly">
            <v-card>
                <v-card-title class="error white--text">
                    <v-icon left dark>mdi-alert-circle</v-icon>
                    반려 사유 입력
                </v-card-title>
                <v-card-text class="pt-6">
                    <v-textarea
                        v-model="rejectReason"
                        label="반려 사유를 입력해주세요"
                        outlined
                        rows="4"
                        placeholder="예: 이미지가 불분명함, 도용된 이미지 등"
                        hide-details
                    ></v-textarea>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn text @click="rejectDialog = false">취소</v-btn>
                    <v-btn color="error" class="px-6" @click="ExecuteReject" :disabled="!rejectReason.trim()">반려 처리</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000" top>
            {{ snackbarText }}
        </v-snackbar>
    </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
    name: 'Quizncash_Game_Event_Verify',

    data: () => ({
        loadingData: false,
        viewMode: 'not_verify',
        selectedPids: [],
        selectAll: false,
        review_list: [],

        // 리뷰 타입 필터 (퀴즈앤캐시 REVIEW_TYPE: WIDGET=위젯리뷰, MARKET=마켓리뷰)
        reviewTypeFilter: 'ALL',
        reviewTypeFilterOptions: [
            { text: '전체', value: 'ALL' },
            { text: '위젯리뷰', value: 'WIDGET' },
            { text: '마켓리뷰', value: 'MARKET' },
        ],

        detailDialog: false,
        selectedDetailItem: null,

        rejectDialog: false,
        rejectReason: '',
        rejectTargetPids: '',

        snackbar: false,
        snackbarText: '',
        snackbarColor: 'success'
    }),

    created() {
        this.LoadList();
    },

    methods: {
        ...mapActions([
            'QUIZNCASH_GET_REVIEW_LIST_NOT_VERIFY',
            'QUIZNCASH_GET_REVIEW_LIST_VERIFY_ALL',
        ]),

        async LoadList() {
            this.loadingData = true;
            this.selectedPids = [];
            this.selectAll = false;
            try {
                let body_data;
                const payload = { review_type: this.reviewTypeFilter };
                if (this.viewMode === 'not_verify') {
                    body_data = await this.QUIZNCASH_GET_REVIEW_LIST_NOT_VERIFY(payload);
                } else {
                    body_data = await this.QUIZNCASH_GET_REVIEW_LIST_VERIFY_ALL(payload);
                }
                const data = body_data && body_data.response_data ? body_data.response_data : body_data;
                this.review_list = (data && data.review_list) || [];
            } catch (err) {
                console.error(err);
                this.ShowSnackbar('데이터를 불러오는데 실패했습니다.', 'error');
            } finally {
                this.loadingData = false;
            }
        },

        GetItemImage(item) {
            const path = item.IMAGE || item.IMAGE_URL;
            if (path) {
                if (path.startsWith('http')) return path;
                return 'https://content-filecenter.s3.ap-northeast-2.amazonaws.com/' + path;
            }
            return '';
        },

        ToggleSelect(pid) {
            const index = this.selectedPids.indexOf(pid);
            if (index > -1) {
                this.selectedPids.splice(index, 1);
            } else {
                this.selectedPids.push(pid);
            }
            this.selectAll = this.selectedPids.length === this.review_list.length && this.review_list.length > 0;
        },

        IsSelected(pid) {
            return this.selectedPids.includes(pid);
        },

        ToggleAll() {
            if (this.selectAll) {
                this.selectedPids = this.review_list.map((item) => item.PID);
            } else {
                this.selectedPids = [];
            }
        },

        ViewDetail(item) {
            this.selectedDetailItem = item;
            this.detailDialog = true;
        },

        async ProcessSelection(state) {
            const pids = this.selectedPids.join(',');
            if (state === 'R') {
                this.OpenRejectDialog(pids);
            } else {
                await this.ExecuteApprove(pids);
            }
        },

        async ProcessItem(pid, state) {
            if (state === 'R') {
                this.OpenRejectDialog(String(pid));
            } else {
                await this.ExecuteApprove(String(pid));
            }
        },

        OpenRejectDialog(pids) {
            this.rejectTargetPids = pids;
            this.rejectReason = '리뷰 이미지가 요건에 맞지 않습니다.';
            this.rejectDialog = true;
        },

        async ExecuteReject() {
            if (!this.rejectReason.trim()) return;
            this.loadingData = true;
            try {
                await this.$store.dispatch('QUIZNCASH_SET_REVIEW_LIST_REFUSE', {
                    pids: this.rejectTargetPids,
                    rejection_reason: this.rejectReason.trim()
                });
                this.ShowSnackbar('반려 처리되었습니다.', 'success');
                this.rejectDialog = false;
                await this.LoadList();
                this.$root.$emit('menu-dot:refresh');   // 메뉴 레드닷 갱신
            } catch (err) {
                console.error(err);
                this.ShowSnackbar('처리 중 오류가 발생했습니다.', 'error');
            } finally {
                this.loadingData = false;
            }
        },

        async ExecuteApprove(pids) {
            if (!confirm('선택한 항목을 인증하시겠습니까? (보상이 지급됩니다)')) return;
            this.loadingData = true;
            try {
                await this.$store.dispatch('QUIZNCASH_SET_REVIEW_LIST_APPROVE', { pids });
                this.ShowSnackbar('인증 처리되었습니다.', 'success');
                this.detailDialog = false;
                await this.LoadList();
                this.$root.$emit('menu-dot:refresh');   // 메뉴 레드닷 갱신
            } catch (err) {
                console.error(err);
                this.ShowSnackbar('처리 중 오류가 발생했습니다.', 'error');
            } finally {
                this.loadingData = false;
            }
        },

        // 유저 표시 통일: 닉네임(USER_ID). 닉네임 없으면 USER_ID 만.
        UserLabel(item) {
            if (!item) return '';
            const nick = item.USER_NICK || '';
            const uid = item.USER_ID || '';
            if (nick && uid) return `${nick}(${uid})`;
            return nick || uid || '-';
        },

        GetStatusColor(status) {
            if (status === 'Y') return 'success';
            if (status === 'R' || status === 'C') return 'error';
            return 'warning';
        },

        GetStatusText(status) {
            if (status === 'Y') return '인증완료';
            if (status === 'R' || status === 'C') return '반려됨';
            return '대기중';
        },

        // 미인증(대기) 상태: N(신규/기본값) 또는 W(대기)
        IsPending(status) {
            return status === 'N' || status === 'W';
        },

        GetReviewTypeText(type) {
            if (type === 'MARKET') return '마켓리뷰';
            return '위젯리뷰';
        },

        GetReviewTypeColor(type) {
            if (type === 'MARKET') return 'deep-purple';
            return 'teal';
        },

        ShowSnackbar(text, color) {
            this.snackbarText = text;
            this.snackbarColor = color || 'success';
            this.snackbar = true;
        }
    },

    watch: {
        '$store.state.selected_game_package'() {
            this.LoadList();
        }
    }
};
</script>

<style scoped>
@media (max-width: 600px) {
  .v-toolbar__content { flex-wrap: wrap; height: auto !important; padding: 8px; gap: 4px; }
}

.mode-toggle {
    background-color: rgba(0, 0, 0, 0.2) !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    border-radius: 8px !important;
    overflow: hidden;
}

.active-mode {
    background-color: #fb8c00 !important;
    color: white !important;
    font-weight: bold !important;
}

.active-mode .v-icon {
    color: white !important;
}

.review-card {
    transition: all 0.2s ease-in-out;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    border: 2px solid transparent !important;
}

.review-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15) !important;
}

.selected-card {
    border-color: #0277bd !important;
    background-color: #e1f5fe !important;
}

.readonly-card {
    cursor: default !important;
}

.readonly-card:hover {
    transform: none !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
}

.image-container {
    position: relative;
    background-color: #f5f5f5;
}

.selection-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(1, 87, 155, 0.4);
}

.zoom-preview {
    position: absolute;
    z-index: 999;
    top: 0;
    left: 100%;
    margin-left: 10px;
}

@media (max-width: 960px) {
    .zoom-preview {
        left: 0;
        top: 100%;
        margin-left: 0;
        margin-top: 10px;
    }
}
</style>
